/**
 * childToProjectData 工具
 * 用于获取项目数据的父子关系和生成唯一ID
 */
import { getParent, ExtractedObjectTypeValues } from "@fanfanlo";
import { ITrigger } from '../entities/ITrigger'
import { ITriggerGroup } from '../entities/ITriggerGroup'
import { IAction } from '../entities/IAction'
import { IActionGroup } from '../entities/IActionGroup'
import { IProject } from '../entities/IProject'
import { ITriggerAction } from '../entities/ITriggerAction'
import { ITriggerActionGroup } from '../entities/ITriggerActionGroup'

export interface IChildToProjectData {
    action?: IAction
    actionList?: IAction[]
    actionGroup?: IActionGroup

    trigger?: ITrigger
    triggerList?: ITrigger[]
    triggerGroup?: ITriggerGroup

    triggerAction?: ITriggerAction
    triggerActionList?: ITriggerAction[]
    triggerActionGroup?: ITriggerActionGroup

    project?: IProject
}

export function projectDataToUniqueId(data: IChildToProjectData): string {
    let s = ``
    function add(name: string, id?: string) { if (id) s += `|${name}:${id}` }
    add('project', data.project?.ulid);
    add('trigger-action-group', data.triggerActionGroup?.ulid);
    add('trigger-action', data.triggerAction?.ulid);

    add('trigger-group', data.triggerGroup?.ulid);
    add('trigger', data.trigger?.ulid);
    add('trigger-java-data', data.trigger?.javaData?.ulid)

    add('action-group', data.actionGroup?.ulid);
    add('action', data.action?.ulid);
    add('action-java-data', data.action?.javaData?.ulid)
    return s;
}

// project
function getProjectByProp<T extends IProject = IProject>(prop: ExtractedObjectTypeValues<T>, projectData?: IChildToProjectData): T | undefined {
    const data = prop ? getParent<T>(prop) : undefined
    if (projectData) {
        projectData.project = data
    }
    return data
}

function getProjectElementByProp<T extends IProject = IProject>(prop: ExtractedObjectTypeValues<T>, prefix: string = '', suffix: string = '', projectData: IChildToProjectData = {}) {
    getProjectByProp(prop, projectData)
    return getProjectElementsId(projectData, prefix, suffix)
}


// trigger action group
function getTriggerActionGroupByProp<T extends ITriggerActionGroup = ITriggerActionGroup>(prop: ExtractedObjectTypeValues<T>, projectData?: IChildToProjectData) {
    const data = prop ? getParent<T>(prop) : undefined
    if (projectData && data) {
        projectData.triggerActionGroup = data
        getProjectByProp(data, projectData)
    }
    return data
}

function getTriggerActionGroupElementByProp<T extends ITriggerActionGroup = ITriggerActionGroup>(prop: ExtractedObjectTypeValues<T>, prefix: string = '', suffix: string = '', projectData: IChildToProjectData = {}) {
    getTriggerActionGroupByProp(prop, projectData)
    return getProjectElementsId(projectData, prefix, suffix)
}


function getTriggerActionGroupElement(triggerActionGroup: ITriggerActionGroup, prefix: string = '', suffix: string = '', projectData: IChildToProjectData = {}) {
    projectData.triggerActionGroup = triggerActionGroup
    getTriggerActionGroupByProp(triggerActionGroup.checks, projectData)
    return getProjectElementsId(projectData, prefix, suffix)
}

// trigger action list
function getTriggerActionListByProp<T extends ITriggerAction = ITriggerAction>(prop: T, projectData?: IChildToProjectData) {
    const data = prop ? getParent<T[]>(prop) : undefined
    if (projectData && data) {
        projectData.triggerActionList = data
        getTriggerActionGroupByProp(data, projectData)
    }
    return data
}

function getTriggerActionListElementByProp<T extends ITriggerAction = ITriggerAction>(prop: T, prefix: string = '', suffix: string = '', projectData: IChildToProjectData = {}) {
    getTriggerActionListByProp(prop, projectData)
    return getProjectElementsId(projectData, prefix, suffix)
}

// trigger action
function getTriggerActionByProp<T extends ITriggerAction = ITriggerAction>(prop: ExtractedObjectTypeValues<T>, projectData?: IChildToProjectData) {
    const data = prop ? getParent<T>(prop) : undefined
    if (projectData && data) {
        projectData.triggerAction = data
        getTriggerActionListByProp(data, projectData)
    }
    return data
}

function getTriggerActionElementByProp<T extends ITriggerAction = ITriggerAction>(prop: ExtractedObjectTypeValues<T>, prefix: string = '', suffix: string = '', projectData: IChildToProjectData = {}) {
    getTriggerActionByProp(prop, projectData)
    return getProjectElementsId(projectData, prefix, suffix)
}

// trigger group
function getTriggerGroupByProp<T extends ITriggerGroup = ITriggerGroup>(prop: ExtractedObjectTypeValues<T>, projectData?: IChildToProjectData) {
    const data = prop ? getParent<T>(prop) : undefined
    if (projectData && data) {
        projectData.triggerGroup = data
        getTriggerActionByProp(data, projectData)
    }
    return data
}

function getTriggerGroupElementByProp<T extends ITriggerGroup = ITriggerGroup>(prop: ExtractedObjectTypeValues<T>, prefix: string = '', suffix: string = '', projectData: IChildToProjectData = {}) {
    getTriggerGroupByProp(prop, projectData)
    return getProjectElementsId(projectData, prefix, suffix)
}

function getTriggerGroupElement(triggerGroup: ITriggerGroup, prefix: string = '', suffix: string = '', projectData: IChildToProjectData = {}) {
    projectData.triggerGroup = triggerGroup
    getTriggerGroupByProp(triggerGroup.list, projectData)
    return getProjectElementsId(projectData, prefix, suffix)
}

// trigger list
export function getTriggerListByProp<T extends ITrigger = ITrigger>(prop: ExtractedObjectTypeValues<T>, projectData?: IChildToProjectData) {
    const data = prop ? getParent<T[]>(prop) : undefined
    if (projectData && data) {
        projectData.triggerList = data
        getTriggerGroupByProp(data, projectData)
    }
    return data
}

export function getTriggerListElementByProp<T extends ITrigger = ITrigger>(prop: ExtractedObjectTypeValues<T>, prefix: string = '', suffix: string = '', projectData: IChildToProjectData = {}) {
    getTriggerListByProp(prop, projectData)
    return getProjectElementsId(projectData, prefix, suffix)
}

// trigger
export function getTriggerByProp<T extends ITrigger = ITrigger>(prop: ExtractedObjectTypeValues<T>, projectData?: IChildToProjectData) {
    const data = prop ? getParent<T>(prop) : undefined
    if (projectData && data) {
        projectData.trigger = data
        getTriggerListByProp(data, projectData)
    }
    return data
}

export function getTriggerElementByProp<T extends ITrigger = ITrigger>(prop: ExtractedObjectTypeValues<T>, prefix: string = '', suffix: string = '', projectData: IChildToProjectData = {}) {
    getTriggerByProp(prop, projectData)
    return getProjectElementsId(projectData, prefix, suffix)
}

// action group
export function getActionGroupByProp<T extends IActionGroup = IActionGroup>(prop: ExtractedObjectTypeValues<T>, projectData?: IChildToProjectData) {
    const data = prop ? getParent<T>(prop) : undefined
    if (projectData && data) {
        projectData.actionGroup = data
        getTriggerActionByProp(data, projectData)
    }
    return data
}

export function getActionGroupElementByProp<T extends IActionGroup = IActionGroup>(prop: ExtractedObjectTypeValues<T>, prefix: string = '', suffix: string = '', projectData: IChildToProjectData = {}) {
    getActionGroupByProp(prop, projectData)
    return getProjectElementsId(projectData, prefix, suffix)
}

export function getActionGroupElement(actionGroup: IActionGroup, prefix: string = '', suffix: string = '', projectData: IChildToProjectData = {}) {
    projectData.actionGroup = actionGroup
    getTriggerActionByProp(actionGroup, projectData)
    return getProjectElementsId(projectData, prefix, suffix)
}

// action list
export function getActionListByProp<T extends IAction = IAction>(prop: ExtractedObjectTypeValues<T>, projectData?: IChildToProjectData) {
    const data = prop ? getParent<T[]>(prop) : undefined
    if (projectData && data) {
        projectData.actionList = data
        getActionGroupByProp(data, projectData)
    }
    return data
}

function getActionListElementByProp<T extends IAction = IAction>(prop: ExtractedObjectTypeValues<T>, prefix: string = '', suffix: string = '', projectData: IChildToProjectData = {}) {
    getActionListByProp(prop, projectData)
    return getProjectElementsId(projectData, prefix, suffix)
}

// action
function getActionByProp<T extends IAction = IAction>(prop: ExtractedObjectTypeValues<T>, projectData?: IChildToProjectData) {
    const data = prop ? getParent<T>(prop) : undefined
    if (projectData && data) {
        projectData.action = data
        getActionListByProp(data, projectData)
    }
    return data
}

function getActionElementIdByProp<T extends IAction = IAction>(prop: ExtractedObjectTypeValues<T>, prefix: string = '', suffix: string = '', projectData: IChildToProjectData = {}) {
    getActionByProp(prop, projectData)
    return getProjectElementsId(projectData, prefix, suffix)
}

function getProjectElementsId(childData: IChildToProjectData, prefix: string = '', suffix: string = '') {
    return {
        [`data-groupof${prefix ? '-' + prefix : ''}-project-ulid${suffix ? '-' + suffix : ''}`]: childData.project?.ulid,
        [`data-project-ulid`]: childData.project?.ulid,

        [`data-groupof${prefix ? '-' + prefix : ''}-trigger-action-group-ulid${suffix ? '-' + suffix : ''}`]: childData.triggerActionGroup?.ulid,
        [`data-trigger-action-group-ulid`]: childData.triggerActionGroup?.ulid,

        [`data-groupof${prefix ? '-' + prefix : ''}-trigger-action-ulid${suffix ? '-' + suffix : ''}`]: childData.triggerAction?.ulid,
        [`data-trigger-action-ulid`]: childData.triggerAction?.ulid,

        [`data-groupof${prefix ? '-' + prefix : ''}-trigger-group-ulid${suffix ? '-' + suffix : ''}`]: childData.triggerGroup?.ulid,
        [`data-trigger-group-ulid`]: childData.triggerGroup?.ulid,

        [`data-groupof${prefix ? '-' + prefix : ''}-trigger-ulid${suffix ? '-' + suffix : ''}`]: childData.trigger?.ulid,
        [`data-trigger-ulid`]: childData.trigger?.ulid,

        [`data-groupof${prefix ? '-' + prefix : ''}-action-group-ulid${suffix ? '-' + suffix : ''}`]: childData.actionGroup?.ulid,
        [`data-action-group-ulid`]: childData.actionGroup?.ulid,

        [`data-groupof${prefix ? '-' + prefix : ''}-action-ulid${suffix ? '-' + suffix : ''}`]: childData.action?.ulid,
        [`data-action-ulid`]: childData.action?.ulid
    }
}

export const childToProjectDataUtils = {
    project: {
        getProjectByProp,
        getProjectElementByProp
    },
    triggerActionGroup: {
        getTriggerActionGroupByProp,
        getTriggerActionGroupElementByProp,
        getTriggerActionGroupElement,
    },
    triggerActionList: {
        getTriggerActionListByProp,
        getTriggerActionListElementByProp,
    },
    triggerAction: {
        getTriggerActionByProp,
        getTriggerActionElementByProp,
    },
    triggerGroup: {
        getTriggerGroupByProp,
        getTriggerGroupElementByProp,
        getTriggerGroupElement
    },
    trigger: {
        getTriggerByProp,
        getTriggerElementByProp,
    },
    triggerList: {
        getTriggerListByProp,
        getTriggerListElementByProp,
    },
    actionGroup: {
        getActionGroupByProp,
        getActionGroupElementByProp,
        getActionGroupElement,
    },
    actionList: {
        getActionListByProp,
        getActionListElementByProp,
    },
    action: {
        getActionByProp,
        getActionElementIdByProp
    },
    getProjectElementsId
}
